import React, { useState, useEffect, useCallback } from 'react';
import { fehlertext } from '../lib/fehlertext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Progress } from '../components/ui/progress';
import { Clock, AlertCircle, Loader2, ArrowLeft, CheckCircle2, WifiOff, Mail, Lock } from 'lucide-react';
import { AnmeldeRahmen } from '../components/anmeldung/AnmeldeRahmen';
import { Titel, Unterzeile, Beschriftung, Feld, Auge, Hauptknopf, Fehlerkasten, TextLink } from '../components/anmeldung/Bausteine';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Password strength checker
const checkPasswordStrength = (password) => {
  let strength = 0;
  let feedback = [];

  if (password.length >= 8) { strength += 25; } else { feedback.push('Mindestens 8 Zeichen'); }
  if (/[a-z]/.test(password)) { strength += 25; } else { feedback.push('Kleinbuchstaben'); }
  if (/[A-Z]/.test(password)) { strength += 25; } else { feedback.push('Großbuchstaben'); }
  if (/[0-9]/.test(password) || /[^a-zA-Z0-9]/.test(password)) { strength += 25; } else { feedback.push('Zahlen oder Sonderzeichen'); }

  let label = 'Sehr schwach';
  if (strength >= 100) label = 'Stark';
  else if (strength >= 75) label = 'Gut';
  else if (strength >= 50) label = 'Mittel';
  else if (strength >= 25) label = 'Schwach';

  return { strength, label, feedback };
};

// Fehler-Detail sicher auslesen: das Backend liefert bei Reset-Fehlern ein Objekt
// {code, message}, bei 422 ein Array, sonst evtl. einen String. Nie ein Objekt in JSX.
const extractDetail = (err) => fehlertext(err, 'Fehler beim Zurücksetzen');

// Wiederverwendbares Anforderungs-Formular mit eigenem Erfolgszustand.
const RequestResetForm = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Backend normalisiert bereits (NormalizedEmail); trim/toLowerCase ist rein kosmetisch.
      await axios.post(`${BACKEND_URL}/api/auth/forgot-password`, { email: email.trim().toLowerCase() });
    } catch (_) {
      // neutral bleiben – nie verraten, ob die E-Mail existiert
    } finally {
      setSent(true);
      setLoading(false);
      toast.success('Link zum Zurücksetzen wurde gesendet (falls E-Mail existiert)');
    }
  };

  if (sent) {
    return (
      <div className="text-center space-y-3" data-testid="request-reset-sent">
        <div className="h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto">
          <Clock className="h-6 w-6 text-blue-600" />
        </div>
        <p className="text-slate-600 text-sm">
          Falls ein Konto mit dieser E-Mail existiert, haben wir Ihnen einen Link zum Zurücksetzen gesendet.
          <br />
          <span className="text-slate-500">Der Link ist 72 Stunden gültig.</span>
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-5 text-left">
      <div>
        <Beschriftung htmlFor="req-email">E-Mail-Adresse</Beschriftung>
        <Feld
          id="req-email"
          type="email"
          symbol={Mail}
          placeholder="name@beispiel.de"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          data-testid="forgot-email-input"
        />
      </div>
      <Hauptknopf type="submit" disabled={loading} data-testid="send-reset-link-button">
        {loading ? (<><Loader2 className="h-4 w-4 animate-spin" />Wird gesendet...</>) : 'Link senden'}
      </Hauptknopf>
    </form>
  );
};

// F1: Selbstheilung — aus einem alten Link einen neuen an die hinterlegte Adresse anfordern
const ResendPanel = ({ token, info }) => {
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const send = async () => {
    setLoading(true);
    try { await axios.post(`${BACKEND_URL}/api/auth/resend-from-token`, { token }); } catch (_) {}
    setSent(true); setLoading(false);
  };
  if (sent) {
    return (
      <div className="text-sm text-slate-600 bg-blue-50 border border-blue-100 rounded p-3" data-testid="resend-sent">
        E-Mail ist unterwegs. Bitte öffne die <strong>neueste</strong> E-Mail von TimeNYzer — ältere E-Mails funktionieren nicht mehr.
      </div>
    );
  }
  return (
    <div className="space-y-2">
      {info?.link_created && (
        <p className="text-xs text-slate-400" data-testid="link-created-info">
          Dieser Link wurde am {new Date(info.link_created).toLocaleDateString('de-DE')} erstellt.
        </p>
      )}
      <Hauptknopf type="button" onClick={send} disabled={loading} data-testid="resend-link-button">
        {loading ? (<><Loader2 className="h-4 w-4 animate-spin" />Wird gesendet...</>) : `Neuen Link an ${info?.masked_email || 'deine Adresse'} senden`}
      </Hauptknopf>
      <TextLink to="/zugangscode" className="block text-center" testid="have-access-code-link">
        Ich habe einen Zugangscode von der Verwaltung
      </TextLink>
    </div>
  );
};

const Shell = ({ children }) => <AnmeldeRahmen>{children}</AnmeldeRahmen>;

// Zustandsansicht (Symbol, Überschrift, Text) im Stil der Anmeldeseite.
const Zustand = ({ testid, symbol: Symbol, farbe, titel, children }) => (
  <div className="space-y-4" data-testid={testid}>
    <div className={`flex h-12 w-12 items-center justify-center rounded-full ${farbe}`}>
      <Symbol className="h-6 w-6" />
    </div>
    <Titel>{titel}</Titel>
    {children}
  </div>
);

const ZurueckLink = ({ testid }) => (
  <TextLink to="/login" className="inline-flex items-center" testid={testid}>
    <ArrowLeft className="h-4 w-4 mr-1" />Zurück zur Anmeldung
  </TextLink>
);

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const resetToken = searchParams.get('token');

  const [checkState, setCheckState] = useState('checking');
  // 'checking' | 'ok' | 'expired' | 'used' | 'superseded' | 'unknown' | 'rate_limited' | 'network'
  const [resendInfo, setResendInfo] = useState(null);
  // { can_resend, masked_email, link_created }

  const verifyToken = useCallback(async () => {
    if (!resetToken) return;
    setCheckState('checking');
    try {
      await axios.get(`${BACKEND_URL}/api/auth/verify-reset-token/${resetToken}`);
      setCheckState('ok');
    } catch (err) {
      if (!err.response) { setCheckState('network'); return; }     // CORS, offline, Proxy
      if (err.response.status === 429) { setCheckState('rate_limited'); return; }
      if (err.response.status === 404 || err.response.status >= 500) { setCheckState('network'); return; }
      const d = err.response.data?.detail;
      setResendInfo(d && typeof d === 'object' ? d : null);
      setCheckState(d?.code || 'unknown');
    }
  }, [resetToken]);

  useEffect(() => { verifyToken(); }, [verifyToken]);

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resetComplete, setResetComplete] = useState(false);
  const [error, setError] = useState('');

  const passwordStrength = checkPasswordStrength(newPassword);

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmPassword) {
      setError('Die Passwörter stimmen nicht überein');
      return;
    }
    if (passwordStrength.strength < 50) {
      setError('Bitte ein stärkeres Passwort wählen');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/auth/reset-password`, {
        token: resetToken,
        new_password: newPassword,
      });
      setResetComplete(true);
      toast.success('Passwort erfolgreich zurückgesetzt!');
    } catch (err) {
      const message = extractDetail(err);
      const code = err.response?.data?.detail?.code;
      if (code === 'expired' || code === 'used') setCheckState(code);
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  // ===== Token-Fluss =====
  if (resetToken) {
    // 1) Prüfung läuft
    if (checkState === 'checking') {
      return (
        <Shell>
          <div className="py-10 text-center" data-testid="token-checking">
            <Loader2 className="h-8 w-8 text-blue-700 animate-spin mx-auto mb-4" />
            <p className="text-[#4b5768]">Link wird geprüft …</p>
          </div>
        </Shell>
      );
    }

    // 2) Erfolgreich zurückgesetzt
    if (resetComplete) {
      return (
        <Shell>
          <Zustand symbol={CheckCircle2} farbe="bg-green-100 text-green-600" titel="Passwort zurückgesetzt!">
            <p className="text-[#4b5768]">Sie können sich jetzt mit Ihrem neuen Passwort anmelden.</p>
            <Hauptknopf type="button" onClick={() => navigate('/login')} data-testid="go-to-login-button">
              Zur Anmeldung
            </Hauptknopf>
          </Zustand>
        </Shell>
      );
    }

    // 3) Netzwerkfehler – Link ist wahrscheinlich in Ordnung
    if (checkState === 'network') {
      return (
        <Shell>
          <Zustand testid="reset-error-network" symbol={WifiOff} farbe="bg-amber-100 text-amber-600" titel="Verbindung fehlgeschlagen">
            <p className="text-[#4b5768]">Der Server war nicht erreichbar. Dein Link ist wahrscheinlich in Ordnung.</p>
            <Hauptknopf type="button" onClick={verifyToken} data-testid="retry-verify-button">
              Erneut versuchen
            </Hauptknopf>
            <p className="text-xs text-slate-400">Tipp: Seite mit Strg+F5 komplett neu laden.</p>
          </Zustand>
        </Shell>
      );
    }

    // 4) Rate-Limit – Link bleibt gültig
    if (checkState === 'rate_limited') {
      return (
        <Shell>
          <Zustand testid="reset-error-rate_limited" symbol={Clock} farbe="bg-amber-100 text-amber-600" titel="Zu viele Versuche">
            <p className="text-[#4b5768]">Bitte eine Minute warten. Dein Link ist weiterhin gültig.</p>
            <Hauptknopf type="button" onClick={verifyToken} data-testid="retry-verify-button">
              Erneut prüfen
            </Hauptknopf>
          </Zustand>
        </Shell>
      );
    }

    // 5) used – bereits verwendet
    if (checkState === 'used') {
      return (
        <Shell>
          <Zustand testid="reset-error-used" symbol={CheckCircle2} farbe="bg-slate-100 text-slate-500" titel="Link wurde bereits verwendet">
            <p className="text-[#4b5768]">Dein Passwort ist damit vermutlich schon gesetzt.</p>
            <Hauptknopf type="button" onClick={() => navigate('/login')} data-testid="error-to-login-button">
              Zum Login
            </Hauptknopf>
            <div className="pt-4 border-t border-[#e2e8f0]">
              <p className="text-sm text-[#4b5768] mb-3">Passwort doch nicht gesetzt? Neuen Link anfordern:</p>
              {resendInfo?.can_resend ? <ResendPanel token={resetToken} info={resendInfo} /> : <RequestResetForm />}
            </div>
          </Zustand>
        </Shell>
      );
    }

    // 6) Gültiger Link (checkState === 'ok') → Passwort-Formular
    if (checkState === 'ok') {
      return (
        <Shell>
          <Titel>Neues Passwort festlegen</Titel>
          <Unterzeile>Geben Sie Ihr neues Passwort ein</Unterzeile>
          <div className="mt-6">
            <form onSubmit={handleResetPassword} className="space-y-5">
              {error && <Fehlerkasten testid="reset-password-error">{error}</Fehlerkasten>}

              <div>
                <Beschriftung htmlFor="newPassword">Neues Passwort</Beschriftung>
                <Feld
                  id="newPassword"
                  type={showPassword ? 'text' : 'password'}
                  symbol={Lock}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  data-testid="new-password-input"
                  rechts={<Auge sichtbar={showPassword} onClick={() => setShowPassword(!showPassword)} testid="toggle-new-password-visibility" />}
                />

                {newPassword && (
                  <div className="mt-2 space-y-2">
                    <div className="flex items-center gap-2">
                      <Progress value={passwordStrength.strength} className="h-2 flex-1" />
                      <span className={`text-xs font-medium ${
                        passwordStrength.strength >= 75 ? 'text-green-600' :
                        passwordStrength.strength >= 50 ? 'text-amber-600' : 'text-red-600'
                      }`}>
                        {passwordStrength.label}
                      </span>
                    </div>
                    {passwordStrength.feedback.length > 0 && (
                      <p className="text-xs text-slate-500">Fehlt: {passwordStrength.feedback.join(', ')}</p>
                    )}
                  </div>
                )}
              </div>

              <div>
                <Beschriftung htmlFor="confirmPassword">Passwort bestätigen</Beschriftung>
                <Feld
                  id="confirmPassword"
                  type={showPassword ? 'text' : 'password'}
                  symbol={Lock}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  data-testid="confirm-password-input"
                />
              </div>

              <Hauptknopf type="submit" disabled={loading} data-testid="reset-password-button">
                {loading ? (<><Loader2 className="h-4 w-4 animate-spin" />Wird gespeichert...</>) : 'Passwort speichern'}
              </Hauptknopf>
            </form>
          </div>
        </Shell>
      );
    }

    // 7) expired / superseded / unknown – neuen Link anfordern (Selbstheilung wenn möglich)
    if (checkState === 'expired' || checkState === 'superseded' || checkState === 'unknown') {
      return (
        <Shell>
          <Zustand testid={`reset-error-${checkState}`} symbol={AlertCircle} farbe="bg-red-100 text-red-600"
            titel={checkState === 'expired' ? 'Link ist abgelaufen'
              : checkState === 'superseded' ? 'Link ist veraltet'
              : 'Link ungültig'}>
            <p className="text-[#4b5768]">
              {checkState === 'expired'
                ? 'Links sind 72 Stunden gültig.'
                : checkState === 'superseded'
                ? 'Dieser Link stammt aus einer älteren E-Mail und wurde durch einen neueren ersetzt.'
                : 'Dieser Link ist uns nicht bekannt.'}
            </p>
            <div className="pt-4 border-t border-[#e2e8f0]">
              <p className="text-sm text-[#4b5768] mb-3">Neuen Link anfordern:</p>
              {resendInfo?.can_resend ? <ResendPanel token={resetToken} info={resendInfo} /> : <RequestResetForm />}
            </div>
            <ZurueckLink />
          </Zustand>
        </Shell>
      );
    }

    // 8) Unerwarteter Zustand → Netzwerk-/Retry-Ansicht statt „Link ungültig"
    return (
      <Shell>
        <Zustand testid="reset-error-network" symbol={WifiOff} farbe="bg-amber-100 text-amber-600" titel="Verbindung fehlgeschlagen">
          <p className="text-[#4b5768]">Der Server war nicht erreichbar. Dein Link ist wahrscheinlich in Ordnung.</p>
          <Hauptknopf type="button" onClick={verifyToken} data-testid="retry-verify-button">
            Erneut versuchen
          </Hauptknopf>
          <p className="text-xs text-slate-400">Tipp: Seite mit Strg+F5 komplett neu laden.</p>
        </Zustand>
      </Shell>
    );
  }

  // ===== Kein Token: Anforderungs-Seite =====
  return (
    <Shell>
      <Titel testid="forgot-titel">Passwort vergessen?</Titel>
      <Unterzeile>
        Geben Sie Ihre E-Mail-Adresse ein und wir senden Ihnen einen Link zum Zurücksetzen.
      </Unterzeile>
      <div className="mt-6 space-y-5">
        <RequestResetForm />
        <div className="text-center">
          <ZurueckLink testid="back-to-login-link" />
        </div>
      </div>
    </Shell>
  );
};

export default ForgotPassword;
